// Small reusable multi-select for assigning travelers to items (flights, hotels, cars)
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { AuthImage } from "@/components/AuthImage";
import { Users } from "lucide-react";

export function TravelerAssign({ tripId, value, onChange, testId = "traveler-assign" }) {
  const [travelers, setTravelers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/trips/${tripId}/travelers`);
        setTravelers(data);
      } catch (e) {
        console.warn("TravelerAssign load failed:", e?.message || e);
      }
      finally { setLoading(false); }
    })();
  }, [tripId]);

  function toggle(id) {
    const set = new Set(value || []);
    if (set.has(id)) set.delete(id); else set.add(id);
    onChange(Array.from(set));
  }

  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-stone-500 mb-2">
        <Users className="w-3 h-3" /> Assign travelers
      </div>
      {loading ? (
        <div className="h-10 rounded-xl bg-muted animate-pulse" />
      ) : travelers.length === 0 ? (
        <div className="text-xs text-stone-500 italic">Add travelers first to assign them here.</div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {travelers.map((t) => {
            const checked = (value || []).includes(t.id);
            return (
              <label
                key={t.id}
                className={`flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border cursor-pointer transition-colors ${checked ? "bg-primary/10 border-primary text-primary" : "bg-white border-border text-stone-700 hover:bg-muted"}`}
                data-testid={`${testId}-option-${t.id}`}
              >
                <Checkbox checked={checked} onCheckedChange={() => toggle(t.id)} data-testid={`${testId}-checkbox-${t.id}`} />
                <div className="w-6 h-6 rounded-full overflow-hidden border border-border">
                  <AuthImage fileId={t.photo_file_id} className="w-full h-full object-cover" fallbackClassName="w-full h-full" />
                </div>
                <span className="text-sm">{t.name}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function TravelerChips({ tripId, travelerIds }) {
  const [travelers, setTravelers] = useState([]);

  useEffect(() => {
    if (!travelerIds || travelerIds.length === 0) { setTravelers([]); return; }
    (async () => {
      try {
        const { data } = await api.get(`/trips/${tripId}/travelers`);
        setTravelers(data.filter((t) => travelerIds.includes(t.id)));
      } catch (e) {
        console.warn("TravelerChips load failed:", e?.message || e);
      }
    })();
  }, [tripId, travelerIds]);

  if (!travelerIds || travelerIds.length === 0) return null;
  return (
    <div className="mt-3 flex items-center gap-1.5 flex-wrap">
      <div className="flex -space-x-2">
        {travelers.slice(0, 5).map((t) => (
          <div key={t.id} className="w-7 h-7 rounded-full border-2 border-white overflow-hidden bg-muted" title={t.name}>
            <AuthImage fileId={t.photo_file_id} className="w-full h-full object-cover" fallbackClassName="w-full h-full" />
          </div>
        ))}
      </div>
      <span className="text-xs text-stone-500">
        {travelers.map((t) => t.name.split(" ")[0]).slice(0, 3).join(", ")}
        {travelers.length > 3 ? ` +${travelers.length - 3}` : ""}
      </span>
    </div>
  );
}
